import { useState } from 'react';
import { db, storage } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './About.css';
import logo from '../assets/satsat-logo.svg';
import joeyPhoto from '../assets/joey.png';
import hamzaPhoto from '../assets/hamza.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';

const About = () => {
  const [careerForm, setCareerForm] = useState({
    email: '',
    resume: null,
    message: '',
    interests: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const interestAreas = ['Product', 'Engineering', 'Data', 'Marketing', 'Brand', 'Community', 'Finance', 'Strategy'];

  const handleEmailChange = (e) => {
    setCareerForm({ ...careerForm, email: e.target.value });
    setError('');
  };

  const handleResumeChange = (e) => {
    setCareerForm({ ...careerForm, resume: e.target.files[0] });
  };

  const handleMessageChange = (e) => {
    setCareerForm({ ...careerForm, message: e.target.value });
  };

  const handleInterestChange = (area) => {
    const updated = careerForm.interests.includes(area)
      ? careerForm.interests.filter((i) => i !== area)
      : [...careerForm.interests, area];
    setCareerForm({ ...careerForm, interests: updated });
  };

  const handleCareerSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const emailQuery = query(
        collection(db, 'talentPool'),
        where('email', '==', careerForm.email.toLowerCase())
      );
      const existing = await getDocs(emailQuery);

      if (!existing.empty) {
        setError('This email has already been submitted.');
        setSubmitting(false);
        return;
      }

      let resumeUrl = null;

      if (careerForm.resume) {
        const fileRef = ref(storage, `resumes/${Date.now()}_${careerForm.resume.name}`);
        await uploadBytes(fileRef, careerForm.resume);
        resumeUrl = await getDownloadURL(fileRef);
      }

      await addDoc(collection(db, 'talentPool'), {
        email: careerForm.email.toLowerCase(),
        message: careerForm.message,
        interests: careerForm.interests,
        resumeUrl,
        submittedAt: serverTimestamp()
      });

      setSubmitted(true);
      setCareerForm({ email: '', resume: null, message: '', interests: [] });
    } catch (err) {
      console.error('Error submitting application:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="about-container">
      <img src={logo} alt="SatSat Logo" className="about-logo" />
      <h1 className="about-title">About SatSat</h1>
      <p className="about-text">
        SatSat connects people through shared meals. Host a dinner, join a table,
        and discover new flavors and friendships in your neighborhood.
      </p>

      <section className="about-section">
        <h2 className="about-subheader">Meet the Founders</h2>
        <div className="founders-grid">
          <div className="founder-card">
            <img src={joeyPhoto} alt="Joey Soeder" className="founder-avatar" />
            <h3 className="founder-name">Joey Soeder</h3>
            <p className="founder-title">Co-Founder</p>
            <a href="https://www.linkedin.com/in/joeysoeder/" target="_blank" rel="noopener noreferrer" className="founder-linkedin">
              <FontAwesomeIcon icon={faLinkedin} />
            </a>
            <p className="founder-bio">
              Joey Soeder is a Duke University alumnus and co-founder of SatSat. 
              You might find him hunting down the best mezcal, undertaking an endurance challenge, debating coffee origins, or reading the Booker Long list.
              Joey built SatSat because "I wanted the app to exist."
            </p>
          </div>
          <div className="founder-card">
            <img src={hamzaPhoto} alt="Hamza Mirza" className="founder-avatar" />
            <h3 className="founder-name">Hamza Mirza</h3>
            <p className="founder-title">Co-Founder</p>
            <a href="https://www.linkedin.com/in/hamzaamirza/" target="_blank" rel="noopener noreferrer" className="founder-linkedin">
              <FontAwesomeIcon icon={faLinkedin} />
            </a>
            <p className="founder-bio">
              Hamza Mirza is a Duke University alumnus and co-founder of SatSat.
              A coffee aficionado, house music enthusiast, tennis beginner, and devoted fine-dining explorer.
              He believes that the best connections are forged over a shared meal, and SatSat is his way of making sure those moments are never hard to find.
            </p>
          </div>
        </div>
      </section>

      <section className="about-section">
        <h2 className="about-subheader">FAQ</h2>
      </section>

      <section className="about-section">
        <h2 className="about-subheader">Careers</h2>
        <p className="about-text">
          We're always looking for passionate people to join our team. Submit your resume and let us know what areas interest you — we'll reach out when the right opportunity comes up.
        </p>
        <p className="career-warning">
          Please note: This form is for individuals interested in joining our team and not for sales pitches or vendor outreach. Unsolicited service offers will not only be disregarded, but treated with extra contempt.
        </p>
        <form className="career-form" onSubmit={handleCareerSubmit}>
          {error && <p className="career-error">{error}</p>}
          <div className="career-field">
            <label className="career-label">Email</label>
            <input
              type="email"
              value={careerForm.email}
              onChange={handleEmailChange}
              placeholder="your@email.com"
              className="career-input"
              required
            />
          </div>
          <div className="career-field">
            <label className="career-label">Upload Resume</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleResumeChange}
              className="career-file-input"
            />
          </div>
          <div className="career-field">
            <label className="career-label">Message</label>
            <textarea
              value={careerForm.message}
              onChange={handleMessageChange}
              placeholder="Tell us a bit about yourself..."
              className="career-textarea"
              rows={4}
            />
          </div>
          <div className="career-field">
            <label className="career-label">Areas of Interest</label>
            <div className="career-interests">
              {interestAreas.map((area) => (
                <label key={area} className="career-interest-option">
                  <input
                    type="checkbox"
                    checked={careerForm.interests.includes(area)}
                    onChange={() => handleInterestChange(area)}
                  />
                  {area}
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="career-submit-button" disabled={submitting || submitted}>
            {submitting ? 'Submitting...' : submitted ? 'Submitted!' : 'Join Talent Pool'}
          </button>
        </form>
      </section>
    </div>
  );
};

export default About;
